<template>
  <div class="evaluation-stat-v3">
    <!-- Header Section với Tiêu đề và Tóm tắt -->
    <div class="stat-section-header">
      <div class="header-title-group">
        <div class="header-icon-badge">
          <i class="el-icon-chat-line-round"></i>
        </div>
        <div>
          <h2 class="header-title">Thống Kê Nhận Xét & Đánh Giá Học Sinh</h2>
          <p class="header-subtitle">Theo dõi tiến độ nhận xét học sinh theo lớp, khối và toàn trường</p>
        </div>
      </div>

      <!-- Quick summary badge -->
      <div v-if="displayData" class="header-badge-group">
        <span class="live-indicator">
          <span class="pulse-dot"></span>
          Đã nhận xét: {{ currentStats.reviewedCount }}/{{ currentStats.totalStudents }} học sinh ({{ currentStats.rate }}%)
        </span>
      </div>
    </div>

    <div class="evaluation-type-switcher" role="group" aria-label="Loại nhận xét">
      <span class="evaluation-type-label">Loại nhận xét:</span>
      <div class="evaluation-type-options">
        <button v-for="item in evaluationTypes" :key="item.value" type="button"
          :class="['evaluation-type-button', { active: selectedEvaluationType === item.value }]"
          :aria-pressed="selectedEvaluationType === item.value"
          @click="changeEvaluationType(item.value)">{{ item.label }}</button>
      </div>
    </div>

    <!-- Nhận xét ngày có thể được tổng hợp theo ngày, tuần hoặc tháng. -->
    <div v-if="selectedEvaluationType === 'DAILY'" class="filter-control-bar">
      <div class="filter-left">
        <!-- Bộ lọc Khối -->
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-collection-tag"></i> Khối:</span>
          <el-select
            v-model="filterGrade"
            size="small"
            placeholder="Tất cả các khối"
            class="filter-select"
            @change="handleGradeChange"
          >
            <el-option label="Toàn trường (Tất cả các khối)" value="" />
            <el-option v-for="item in availableGradeOptions" :key="item.gradeId"
              :label="item.gradeName" :value="item.gradeId" />
          </el-select>
        </div>

        <!-- Bộ lọc Lớp -->
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-s-home"></i> Lớp:</span>
          <el-select
            v-model="filterClass"
            size="small"
            placeholder="Tất cả các lớp"
            class="filter-select"
            @change="handleClassChange"
          >
            <el-option label="Tất cả các lớp" value="" />
            <el-option v-for="cls in filteredClassOptions" :key="cls.classId"
              :label="cls.className" :value="cls.classId" />
          </el-select>
        </div>

        <!-- Ngày mốc xác định kỳ tổng hợp nhận xét ngày. -->
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-time"></i> Ngày mốc:</span>
          <el-date-picker
            v-model="selectedDate"
            type="date"
            size="small"
            placeholder="Chọn ngày"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            :clearable="false"
            :picker-options="datePickerOptions"
            class="filter-date-picker"
            @change="handleDateChange"
          />
        </div>

        <!-- Scope reset button -->
        <el-button
          v-if="filterGrade || filterClass"
          type="text"
          icon="el-icon-refresh-left"
          size="small"
          class="reset-scope-btn"
          @click="resetFilterScope"
        >
          Xem toàn trường
        </el-button>
      </div>

      <div class="filter-right">
        <span class="period-label"><i class="el-icon-date"></i> Phạm vi thống kê:</span>
        <div class="period-button-group">
          <button v-for="period in periodOptions" :key="period.value" type="button"
            :class="['p-btn', { active: selectedPeriod === period.value }]"
            @click="changePeriod(period.value)">{{ period.label }}</button>
        </div>
      </div>
    </div>

    <template v-else>
      <div class="filter-control-bar">
        <div class="filter-left">
          <div class="filter-chip">
            <span class="chip-label"><i class="el-icon-collection-tag"></i> Khối:</span>
            <el-select v-model="filterGrade" size="small" class="filter-select"
              @change="handleGradeChange">
              <el-option label="Toàn trường (Tất cả các khối)" value="" />
              <el-option v-for="item in availableGradeOptions" :key="item.gradeId"
                :label="item.gradeName" :value="item.gradeId" />
            </el-select>
          </div>
          <div class="filter-chip">
            <span class="chip-label"><i class="el-icon-s-home"></i> Lớp:</span>
            <el-select v-model="filterClass" size="small" class="filter-select"
              @change="handleClassChange">
              <el-option label="Tất cả các lớp" value="" />
              <el-option v-for="cls in filteredClassOptions" :key="cls.classId"
                :label="cls.className" :value="cls.classId" />
            </el-select>
          </div>
          <div v-if="selectedEvaluationType === 'WEEKLY'" class="filter-chip">
            <span class="chip-label"><i class="el-icon-date"></i> Tuần:</span>
            <el-date-picker v-model="selectedWeekDate" type="week" size="small"
              format="Tuần WW/yyyy" placeholder="Chọn tuần" class="filter-date-picker"
              :picker-options="datePickerOptions" :clearable="false" @change="loadPeriod" />
          </div>
          <div v-else-if="selectedEvaluationType === 'MONTHLY'" class="filter-chip">
            <span class="chip-label"><i class="el-icon-date"></i> Tháng:</span>
            <el-date-picker v-model="selectedMonth" type="month" size="small"
              format="MM/yyyy" value-format="yyyy-MM" placeholder="Chọn tháng"
              class="filter-date-picker" :picker-options="datePickerOptions"
              :clearable="false" @change="loadPeriod" />
          </div>
          <template v-else>
            <div class="filter-chip">
              <span class="chip-label"><i class="el-icon-date"></i> Năm dương lịch:</span>
              <el-date-picker v-model="selectedYear" type="year" size="small"
                format="yyyy" value-format="yyyy" placeholder="Chọn năm"
                class="filter-date-picker" :picker-options="datePickerOptions"
                :clearable="false" @change="handleYearChange" />
            </div>
            <div class="filter-chip">
              <span class="chip-label"><i class="el-icon-document"></i> Đợt đánh giá:</span>
              <el-input-number v-model="selectedPeriodNumber" size="small" :min="1" :max="99"
                :controls="true" class="filter-number-picker" @change="loadPeriod" />
            </div>
            <div class="filter-chip">
              <span class="chip-label"><i class="el-icon-time"></i> Ngày mốc:</span>
              <el-date-picker v-model="selectedAnchorDate" type="date" size="small"
                format="dd/MM/yyyy" value-format="yyyy-MM-dd" placeholder="Chọn ngày"
                class="filter-date-picker" :picker-options="periodicDatePickerOptions"
                :clearable="false" @change="loadPeriod" />
            </div>
          </template>
          <el-button v-if="filterGrade || filterClass" type="text" icon="el-icon-refresh-left"
            size="small" class="reset-scope-btn" @click="resetFilterScope">Xem toàn trường</el-button>
        </div>
      </div>
    </template>

    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <div v-if="loading" class="evaluation-data-state">Đang tải thống kê {{ selectedEvaluationLabel.toLowerCase() }}...</div>

    <!-- 2. Cụm Chỉ Số KPI: Tổng số học sinh, Đã nhận xét, Chưa nhận xét -->
    <div v-if="displayData" class="eval-kpi-grid">
      <!-- 1. Tổng số học sinh -->
      <div class="eval-kpi-card border-indigo">
        <div class="kpi-icon-wrap bg-indigo-soft text-indigo">
          <i class="el-icon-user-solid"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Học sinh cần nhận xét</span>
          <div class="kpi-number text-indigo">
            {{ currentStats.totalStudents }} <span class="kpi-unit">học sinh</span>
          </div>
          <div class="kpi-hint">{{ filterScopeLabel }}</div>
        </div>
      </div>

      <!-- 2. Đã nhận xét -->
      <div class="eval-kpi-card border-emerald">
        <div class="kpi-icon-wrap bg-emerald-soft text-emerald">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Đã nhận xét</span>
          <div class="kpi-number text-emerald">
            {{ currentStats.reviewedCount }} <span class="kpi-unit">học sinh</span>
          </div>
          <div class="kpi-hint text-emerald">
            <i class="el-icon-top"></i> Đạt {{ currentStats.rate }}% hoàn thành
          </div>
        </div>
      </div>

      <!-- 3. Chưa nhận xét -->
      <div class="eval-kpi-card border-rose">
        <div class="kpi-icon-wrap bg-rose-soft text-rose">
          <i class="el-icon-warning-outline"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Chưa nhận xét</span>
          <div class="kpi-number text-rose">
            {{ currentStats.unreviewedCount }} <span class="kpi-unit">học sinh</span>
          </div>
          <div class="kpi-hint text-rose">
            <i class="el-icon-bell"></i> Còn {{ currentStats.unreviewedRate }}% chưa làm
          </div>
        </div>
      </div>

      <!-- 4. Thẻ Phụ Trợ: Tỷ lệ đạt chuẩn & Tương tác phụ huynh -->
      <div class="eval-kpi-card border-purple">
        <div class="kpi-icon-wrap bg-purple-soft text-purple">
          <i class="el-icon-chat-dot-round"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Phụ huynh đã đọc</span>
          <div class="kpi-number text-purple">
            {{ currentStats.parentReadCount }} <span class="kpi-unit">học sinh</span>
          </div>
          <div class="kpi-hint">
            Tỷ lệ đã đọc: {{ currentStats.parentReadRate }}% nhận xét đã duyệt
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Khu Vực Biểu Đồ So Sánh & Tiến Độ -->
    <el-row v-if="displayData" :gutter="20" class="charts-row">
      <!-- Biểu đồ 1: Tỷ lệ hoàn thành nhận xét (Gauge) -->
      <el-col :xs="24" :sm="24" :md="8" :lg="7">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Tỷ Lệ Nhận Xét Chung</h3>
              <p class="chart-subtitle">{{ currentPeriodTitle }} • {{ filterScopeLabel }}</p>
            </div>
            <span :class="['tag-pill', currentStats.rate >= 90 ? 'tag-pill--emerald' : 'tag-pill--amber']">
              {{ currentStats.rate >= 90 ? 'Tiến độ tốt' : 'Cần đẩy nhanh' }}
            </span>
          </div>

          <div class="chart-container" ref="chartCompletionGauge"></div>

          <!-- Mini Breakdown Footer -->
          <div class="gauge-footer-grid">
            <div class="mini-stat-card">
              <span class="ms-label">Cần nhận xét</span>
              <strong class="ms-val text-indigo">{{ currentStats.totalStudents }}</strong>
            </div>
            <div class="mini-stat-card">
              <span class="ms-label">Đã nhận xét</span>
              <strong class="ms-val text-emerald">{{ currentStats.reviewedCount }}</strong>
            </div>
            <div class="mini-stat-card">
              <span class="ms-label">Chưa nhận xét</span>
              <strong class="ms-val text-rose">{{ currentStats.unreviewedCount }}</strong>
            </div>
          </div>
        </div>
      </el-col>

      <!-- Biểu đồ 2: So Sánh Tỷ Lệ Nhận Xét Của Từng Lớp Trong Ngày/Tuần/Tháng (Có Cuộn Dọc) -->
      <el-col :xs="24" :sm="24" :md="16" :lg="17">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Tỷ Lệ Nhận Xét Từng Lớp So Với Khối / Trường</h3>
              <p class="chart-subtitle">
                {{ currentPeriodTitle }} • So sánh tỷ lệ % hoàn thành nhận xét giữa các lớp
              </p>
            </div>

            <div class="chart-action-group">
              <!-- Hướng dẫn cuộn dọc khi có nhiều lớp -->
              <span v-if="displayedClassCount > 6" class="scroll-hint-chip">
                <i class="el-icon-sort"></i> Cuộn chuột dọc để xem đủ {{ displayedClassCount }} lớp
              </span>
              <!-- Nhãn chế độ so sánh -->
              <span class="benchmark-badge">
                <i class="el-icon-data-line"></i> Trung bình: <strong>{{ averageBenchmarkRate }}%</strong>
              </span>
            </div>
          </div>

          <div class="chart-container-large" ref="chartClassComparison"></div>

          <!-- Chú giải biểu đồ -->
          <div class="comparison-legend-row">
            <div class="legend-chip">
              <span class="chip-color chip-success"></span>
              <span>Đạt chỉ tiêu (≥ 90%)</span>
            </div>
            <div class="legend-chip">
              <span class="chip-color chip-warning"></span>
              <span>Đang tiến hành (75% - 89%)</span>
            </div>
            <div class="legend-chip">
              <span class="chip-color chip-danger"></span>
              <span>Chậm tiến độ (&lt; 75%)</span>
            </div>
            <div class="legend-chip">
              <span class="chip-line-benchmark"></span>
              <span>Mức trung bình ({{ averageBenchmarkRate }}%)</span>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 4. Bảng Tóm Tắt Chi Tiết Tiến Độ Nhận Xét Từng Lớp (Có thể cuộn dọc) -->
    <div v-if="displayData" class="class-progress-table-card">
      <div :class="['table-card-head', { 'is-expanded': showClassTable }]">
        <div class="head-left">
          <h4 class="table-card-title">
            <i class="el-icon-s-order"></i> Bảng Tiến Độ Nhận Xét Chi Tiết Theo Lớp
          </h4>
          <span class="table-card-subtitle">{{ currentPeriodTitle }} • Hiển thị {{ displayedClassList.length }} lớp</span>
        </div>
        <div class="head-right" style="display: flex; align-items: center; gap: 10px;">
          <span class="table-count-badge">Tổng {{ displayedClassList.length }} lớp</span>
          <el-button
            size="small"
            type="primary"
            plain
            class="table-toggle-btn"
            @click="showClassTable = !showClassTable"
          >
            <i :class="showClassTable ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            {{ showClassTable ? 'Thu gọn bảng' : 'Xem chi tiết bảng' }}
          </el-button>
        </div>
      </div>

      <el-collapse-transition>
        <div v-show="showClassTable" class="table-responsive-wrap">
        <table class="modern-progress-table">
          <thead>
            <tr>
              <th style="width: 50px;">STT</th>
              <th>Tên Lớp</th>
              <th>Khối</th>
              <th>Giáo viên hiện phụ trách</th>
              <th class="text-center" style="width: 110px;">Cần NX</th>
              <th class="text-center" style="width: 110px;">Đã NX</th>
              <th class="text-center" style="width: 110px;">Chưa NX</th>
              <th style="width: 220px;">Tiến độ hoàn thành</th>
              <th class="text-center" style="width: 130px;">Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="displayedClassList.length === 0">
              <td colspan="9" class="text-center">Không có lớp trong phạm vi đã chọn</td>
            </tr>
            <tr
              v-for="(item, idx) in displayedClassList"
              :key="item.classId"
              :class="{ 'row-highlight': filterClass === item.classId }"
            >
              <td class="text-center font-mono">{{ idx + 1 }}</td>
              <td>
                <div class="class-name-cell">
                  <strong>{{ item.className }}</strong>
                  <span v-if="filterClass === item.classId" class="badge-active-class">Đang xem</span>
                </div>
              </td>
              <td>
                <span class="grade-pill">{{ item.gradeName }}</span>
              </td>
              <td class="text-secondary">{{ item.teachers }}</td>
              <td class="text-center font-semibold text-indigo">{{ item.total }}</td>
              <td class="text-center font-semibold text-emerald">{{ item.reviewed }}</td>
              <td class="text-center font-semibold text-rose">{{ item.unreviewed }}</td>
              <td>
                <div class="progress-bar-wrap">
                  <div class="progress-track">
                    <div
                      class="progress-fill"
                      :class="getProgressColorClass(item.rate)"
                      :style="{ width: item.rate + '%' }"
                    ></div>
                  </div>
                  <span class="progress-rate-text font-semibold">{{ item.rate }}%</span>
                </div>
              </td>
              <td class="text-center">
                <span :class="['status-badge', getStatusBadgeClass(item.rate)]">
                  {{ getStatusBadgeLabel(item.rate) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </el-collapse-transition>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import moment from "moment";
import EvaluationDailyStatisticsService from "@/services/dashboard_v3/EvaluationDailyStatisticsService";
import EvaluationPeriodStatisticsService from "@/services/dashboard_v3/EvaluationPeriodStatisticsService";

export default {
  name: "EvaluationStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      showClassTable: false,
      filterGrade: "",
      filterClass: "",
      selectedDate: moment().utcOffset("+07:00").format("YYYY-MM-DD"),
      selectedEvaluationType: "DAILY",
      evaluationTypes: [
        { label: "Nhận xét ngày", value: "DAILY" },
        { label: "Nhận xét tuần", value: "WEEKLY" },
        { label: "Nhận xét tháng", value: "MONTHLY" },
        { label: "Nhận xét định kỳ", value: "PERIODIC" }
      ],
      selectedWeekDate: new Date(),
      selectedMonth: moment().utcOffset("+07:00").format("YYYY-MM"),
      selectedYear: moment().utcOffset("+07:00").format("YYYY"),
      selectedPeriodNumber: 1,
      selectedAnchorDate: moment().utcOffset("+07:00").format("YYYY-MM-DD"),
      selectedPeriod: "DAY",
      periodOptions: [
        { label: "Ngày", value: "DAY" },
        { label: "Tuần", value: "WEEK" },
        { label: "Tháng", value: "MONTH" }
      ],
      gradeOptions: [],
      classOptions: [],
      dailyData: null,
      periodData: null,
      loading: false,
      error: "",
      requestSeq: 0,
      datePickerOptions: {
        disabledDate(date) {
          return moment(date).format("YYYY-MM-DD")
            > moment().utcOffset("+07:00").format("YYYY-MM-DD");
        }
      },
      chartGauge: null,
      chartComparison: null
    };
  },

  computed: {
    selectedEvaluationLabel() {
      const selected = this.evaluationTypes.find(item => item.value === this.selectedEvaluationType);
      return selected ? selected.label : "";
    },
    displayData() {
      return this.selectedEvaluationType === "DAILY" ? this.dailyData : this.periodData;
    },
    availableGradeOptions() {
      return this.displayData ? this.displayData.gradeOptions : this.gradeOptions;
    },
    availableClassOptions() {
      return this.displayData ? this.displayData.classOptions : this.classOptions;
    },
    periodicDatePickerOptions() {
      return {
        disabledDate: date => moment(date).format("YYYY") !== this.selectedYear
          || moment(date).format("YYYY-MM-DD")
            > moment().utcOffset("+07:00").format("YYYY-MM-DD")
      };
    },
    currentPeriodTitle() {
      if (this.selectedEvaluationType === "WEEKLY") {
        const start = this.periodData ? moment(this.periodData.periodStart)
          : moment(this.selectedWeekDate || new Date()).startOf("isoWeek");
        const end = this.periodData ? moment(this.periodData.periodEnd) : start.clone().endOf("isoWeek");
        return `Nhận Xét Tuần ${start.format("DD/MM")} - ${end.format("DD/MM/YYYY")}`;
      }
      if (this.selectedEvaluationType === "MONTHLY") {
        return `Nhận Xét Tháng ${moment(this.selectedMonth, "YYYY-MM").format("MM/YYYY")}`;
      }
      if (this.selectedEvaluationType === "PERIODIC") {
        return `Nhận Xét Định Kỳ · Đợt ${this.selectedPeriodNumber} · Năm ${this.selectedYear} · Ngày mốc ${moment(this.selectedAnchorDate).format("DD/MM/YYYY")}`;
      }
      const date = moment(this.selectedDate);
      const periodStart = this.dailyData ? moment(this.dailyData.periodStart) : date.clone().startOf("isoWeek");
      const periodEnd = this.dailyData ? moment(this.dailyData.periodEnd) : date.clone().endOf("isoWeek");
      if (this.selectedPeriod === "WEEK") {
        return `Tổng Hợp Nhận Xét Ngày · Tuần ${periodStart.format("DD/MM")} - ${periodEnd.format("DD/MM/YYYY")}`;
      }
      if (this.selectedPeriod === "MONTH") {
        const partial = this.dailyData && periodEnd.format("YYYY-MM-DD") !== date.clone().endOf("month").format("YYYY-MM-DD");
        return `Tổng Hợp Nhận Xét Ngày · Tháng ${date.format("MM/YYYY")}${partial ? ` (đến ${periodEnd.format("DD/MM")})` : ""}`;
      }
      return `Nhận Xét Ngày ${date.format("DD/MM/YYYY")}`;
    },
    filteredClassOptions() {
      return this.availableClassOptions.filter(item => !this.filterGrade || item.gradeId === this.filterGrade);
    },
    filterScopeLabel() {
      if (this.filterClass) {
        const cls = this.availableClassOptions.find(item => item.classId === this.filterClass);
        return cls ? `Lớp ${cls.className}` : "Lớp đã chọn";
      }
      if (this.filterGrade) {
        const grade = this.availableGradeOptions.find(item => item.gradeId === this.filterGrade);
        return grade ? grade.gradeName : "Khối đã chọn";
      }
      return "Toàn bộ trường";
    },
    displayedClassCount() {
      return this.displayData ? this.displayData.comparison.length : 0;
    },
    displayedClassList() {
      return this.displayData ? this.displayData.rows : [];
    },
    currentStats() {
      return this.displayData ? this.displayData.summary : {
        totalStudents: 0, reviewedCount: 0, unreviewedCount: 0,
        approvedCount: 0, parentReadCount: 0, rate: 0,
        unreviewedRate: 0, parentReadRate: 0
      };
    },
    averageBenchmarkRate() {
      return this.displayData ? this.displayData.benchmarkRate : 0;
    }
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
    if (this.autoLoad) {
      this.loadData();
    }
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },

  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.loadDaily();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },
    async loadDaily() {
      if (this.selectedEvaluationType !== "DAILY") return;
      const seq = ++this.requestSeq;
      this.loading = true;
      this.error = "";
      this.disposeCharts();
      this.dailyData = null;
      this.periodData = null;
      try {
        const response = await EvaluationDailyStatisticsService.getDaily(
          this.selectedDate, this.filterGrade, this.filterClass, this.selectedPeriod);
        if (seq !== this.requestSeq) return;
        const data = response && response.data && response.data.data;
        if (!data || !Array.isArray(data.rows) || !Array.isArray(data.comparison)
            || !data.summary || !Array.isArray(data.gradeOptions)
            || !Array.isArray(data.classOptions)) {
          throw new Error("Dữ liệu thống kê nhận xét không hợp lệ");
        }
        this.gradeOptions = data.gradeOptions;
        this.classOptions = data.classOptions;
        this.dailyData = data;
        this.$nextTick(this.updateCharts);
      } catch (error) {
        if (seq !== this.requestSeq) return;
        this.error = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message : "Không tải được thống kê nhận xét ngày";
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    async loadPeriod() {
      if (this.selectedEvaluationType === "DAILY") return;
      const seq = ++this.requestSeq;
      this.loading = true;
      this.error = "";
      this.disposeCharts();
      this.periodData = null;
      try {
        let response;
        if (this.selectedEvaluationType === "WEEKLY") {
          response = await EvaluationPeriodStatisticsService.getWeekly(
            moment(this.selectedWeekDate).format("YYYY-MM-DD"), this.filterGrade, this.filterClass);
        } else if (this.selectedEvaluationType === "MONTHLY") {
          response = await EvaluationPeriodStatisticsService.getMonthly(
            this.selectedMonth, this.filterGrade, this.filterClass);
        } else {
          response = await EvaluationPeriodStatisticsService.getPeriodic(
            Number(this.selectedYear), this.selectedPeriodNumber, this.selectedAnchorDate,
            this.filterGrade, this.filterClass);
        }
        if (seq !== this.requestSeq) return;
        const data = response && response.data && response.data.data;
        if (!data || !Array.isArray(data.rows) || !Array.isArray(data.comparison)
            || !data.summary || !Array.isArray(data.gradeOptions)
            || !Array.isArray(data.classOptions)) {
          throw new Error("Dữ liệu thống kê nhận xét không hợp lệ");
        }
        this.gradeOptions = data.gradeOptions;
        this.classOptions = data.classOptions;
        this.periodData = data;
        this.$nextTick(this.updateCharts);
      } catch (error) {
        if (seq !== this.requestSeq) return;
        this.error = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message : "Không tải được thống kê nhận xét";
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    handleYearChange() {
      const selectedYear = Number(this.selectedYear);
      const endOfYear = moment(`${selectedYear}-12-31`, "YYYY-MM-DD");
      const today = moment().utcOffset("+07:00");
      this.selectedAnchorDate = (endOfYear.isAfter(today) ? today : endOfYear)
        .format("YYYY-MM-DD");
      this.loadPeriod();
    },
    handleGradeChange() {
      const selected = this.availableClassOptions.find(item => item.classId === this.filterClass);
      if (selected && selected.gradeId !== this.filterGrade) this.filterClass = "";
      this.refreshCurrentView();
    },
    handleClassChange() {
      const selected = this.availableClassOptions.find(item => item.classId === this.filterClass);
      if (selected) this.filterGrade = selected.gradeId;
      this.refreshCurrentView();
    },
    handleDateChange() {
      this.loadDaily();
    },
    changePeriod(period) {
      if (this.selectedPeriod === period) return;
      this.selectedPeriod = period;
      this.loadDaily();
    },
    changeEvaluationType(type) {
      if (this.selectedEvaluationType === type) return;
      this.requestSeq += 1;
      this.selectedEvaluationType = type;
      this.filterGrade = "";
      this.filterClass = "";
      this.loading = false;
      this.error = "";
      this.disposeCharts();
      this.dailyData = null;
      this.periodData = null;
      this.gradeOptions = [];
      this.classOptions = [];
      this.refreshCurrentView();
    },
    resetFilterScope() {
      this.filterGrade = "";
      this.filterClass = "";
      this.refreshCurrentView();
    },
    refreshCurrentView() {
      if (this.selectedEvaluationType === "DAILY") {
        this.loadDaily();
      } else {
        this.loadPeriod();
      }
    },

    handleResize() {
      if (this.chartGauge) this.chartGauge.resize();
      if (this.chartComparison) this.chartComparison.resize();
    },

    disposeCharts() {
      if (this.chartGauge) {
        this.chartGauge.dispose();
        this.chartGauge = null;
      }
      if (this.chartComparison) {
        this.chartComparison.dispose();
        this.chartComparison = null;
      }
    },

    updateCharts() {
      this.renderGaugeChart();
      this.renderComparisonChart();
    },

    // 1. Render Biểu đồ Gauge Tỷ Lệ Hoàn Thành Nhận Xét
    renderGaugeChart() {
      const dom = this.$refs.chartCompletionGauge;
      if (!dom) return;
      if (!this.chartGauge) {
        this.chartGauge = echarts.init(dom);
      }

      const rate = this.currentStats.rate;

      this.chartGauge.setOption({
        series: [
          {
            type: "gauge",
            startAngle: 210,
            endAngle: -30,
            min: 0,
            max: 100,
            splitNumber: 10,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: "#f59e0b" },
                { offset: 0.5, color: "#3b82f6" },
                { offset: 1, color: "#10b981" }
              ]),
              shadowColor: "rgba(16, 185, 129, 0.35)",
              shadowBlur: 10
            },
            progress: {
              show: true,
              roundCap: true,
              width: 14
            },
            pointer: { show: false },
            axisLine: {
              roundCap: true,
              lineStyle: {
                width: 14,
                color: [[1, "#f1f5f9"]]
              }
            },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            title: {
              show: true,
              offsetCenter: [0, "20%"],
              fontSize: 13,
              color: "#64748b",
              fontWeight: 600
            },
            detail: {
              valueAnimation: true,
              offsetCenter: [0, "-15%"],
              fontSize: 34,
              fontWeight: 800,
              formatter: "{value}%",
              color: "#0f172a"
            },
            data: [
              {
                value: rate,
                name: "Đã nhận xét"
              }
            ]
          }
        ]
      });
    },

    // 2. Render Biểu đồ So Sánh Tỷ Lệ Nhận Xét Của Từng Lớp (Hỗ trợ CUỘN DỌC khi nhiều lớp)
    renderComparisonChart() {
      const dom = this.$refs.chartClassComparison;
      if (!dom) return;
      if (!this.chartComparison) {
        this.chartComparison = echarts.init(dom);
      }

      const listToRender = this.displayData ? this.displayData.comparison : [];

      const totalClasses = listToRender.length;
      const classNames = listToRender.map(c => c.className);
      const benchmark = this.averageBenchmarkRate;

      // Chuẩn bị màu sắc từng cột theo tỷ lệ % và trạng thái đang chọn
      const seriesData = listToRender.map(c => {
        const itemRate = c.rate;
        const isSelected = this.filterClass === c.classId;

        let baseColor = "#3b82f6";
        if (itemRate >= 90) {
          baseColor = "#10b981";
        } else if (itemRate < 75) {
          baseColor = "#f43f5e";
        } else {
          baseColor = "#f59e0b";
        }

        return {
          value: itemRate,
          itemStyle: {
            color: isSelected
              ? new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: "#8b5cf6" },
                  { offset: 1, color: "#6366f1" }
                ])
              : baseColor,
            borderRadius: [0, 8, 8, 0],
            shadowColor: isSelected ? "rgba(99, 102, 241, 0.45)" : "transparent",
            shadowBlur: isSelected ? 8 : 0
          }
        };
      });

      // Thiết lập thanh cuộn dọc DataZoom khi có nhiều hơn 6 lớp
      const maxVisible = 6;
      const needScroll = totalClasses > maxVisible;
      const zoomEndPercent = needScroll ? Math.round((maxVisible / totalClasses) * 100) : 100;

      const dataZoomConfig = [
        // 1. Slider cuộn dọc mỏng đẹp mắt ở lề phải
        {
          type: "slider",
          yAxisIndex: 0,
          show: needScroll,
          width: 8,
          right: 6,
          top: 25,
          bottom: 25,
          start: 0,
          end: zoomEndPercent,
          showDetail: false,
          showDataShadow: false,
          borderColor: "transparent",
          backgroundColor: "#f1f5f9",
          fillerColor: "#94a3b8",
          handleSize: 0,
          zoomLock: false
        },
        // 2. Cuộn chuột trực tiếp (Mouse Wheel scroll)
        {
          type: "inside",
          yAxisIndex: 0,
          disabled: !needScroll,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true,
          moveOnMouseMove: true
        }
      ];

      this.chartComparison.setOption({
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          borderColor: "transparent",
          padding: [12, 16],
          textStyle: { color: "#ffffff", fontSize: 13 },
          formatter: (params) => {
            const idx = params[0].dataIndex;
            const cls = listToRender[idx];
            const pStat = cls;
            const diff = (pStat.rate - benchmark).toFixed(1);
            const diffColor = diff >= 0 ? "#34d399" : "#fb7185";
            const diffPrefix = diff >= 0 ? "+" : "";
            const className = this.escapeHtml(cls.className);
            const gradeName = this.escapeHtml(cls.gradeName);
            const teachers = this.escapeHtml(cls.teachers);

            return `
              <div style="font-weight:700;margin-bottom:6px;color:#f8fafc;border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:5px;">
                ${className} (${gradeName})
              </div>
              <div style="display:flex;justify-content:space-between;gap:18px;margin:4px 0;">
                <span style="color:#94a3b8;">● Tỷ lệ nhận xét:</span>
                <b style="color:#38bdf8;font-size:14px;">${pStat.rate}%</b>
              </div>
              <div style="display:flex;justify-content:space-between;gap:18px;margin:4px 0;">
                <span style="color:#94a3b8;">● Đã nhận xét:</span>
                <b>${pStat.reviewed} / ${cls.total} học sinh</b>
              </div>
              <div style="display:flex;justify-content:space-between;gap:18px;margin:4px 0;">
                <span style="color:#94a3b8;">● Chưa nhận xét:</span>
                <b style="color:#f43f5e;">${pStat.unreviewed} học sinh</b>
              </div>
              <div style="display:flex;justify-content:space-between;gap:18px;margin:4px 0;">
                <span style="color:#94a3b8;">● So với TB (${benchmark}%):</span>
                <b style="color:${diffColor};">${diffPrefix}${diff}%</b>
              </div>
              <div style="margin-top:6px;font-size:11.5px;color:#cbd5e1;border-top:1px dashed rgba(255,255,255,0.15);padding-top:4px;">
                GV: ${teachers}
              </div>
            `;
          }
        },
        grid: {
          top: 25,
          left: 10,
          right: needScroll ? 58 : 45,
          bottom: 15,
          containLabel: true
        },
        dataZoom: dataZoomConfig,
        xAxis: {
          type: "value",
          min: 0,
          max: 100,
          splitNumber: 5,
          axisLabel: {
            formatter: "{value}%",
            color: "#64748b",
            fontSize: 11
          },
          splitLine: {
            lineStyle: { color: "#f1f5f9", type: "dashed" }
          }
        },
        yAxis: {
          type: "category",
          data: classNames,
          inverse: true,
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: {
            color: (val, index) => {
              const cls = listToRender[index];
              return this.filterClass === cls?.classId ? "#4f46e5" : "#334155";
            },
            fontWeight: (val, index) => {
              const cls = listToRender[index];
              return this.filterClass === cls?.classId ? 700 : 600;
            },
            fontSize: 12
          }
        },
        series: [
          // Thanh cột tỷ lệ nhận xét
          {
            name: "Tỷ lệ nhận xét",
            type: "bar",
            barWidth: 16,
            data: seriesData,
            label: {
              show: true,
              position: "right",
              formatter: "{c}%",
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              offset: [4, 0]
            },
            // Đường chuẩn mức trung bình benchmark
            markLine: {
              symbol: "none",
              lineStyle: {
                color: "#6366f1",
                type: "dashed",
                width: 2
              },
              label: {
                show: true,
                position: "insideEndTop",
                formatter: `TB: ${benchmark}%`,
                color: "#4338ca",
                fontWeight: 700,
                fontSize: 11,
                backgroundColor: "#e0e7ff",
                padding: [3, 6],
                borderRadius: 4
              },
              data: [{ xAxis: benchmark }]
            }
          }
        ]
      });
    },

    escapeHtml(value) {
      return String(value == null ? "" : value).replace(/[&<>"']/g, char => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;"
      }[char]));
    },

    // Helper classes cho UI
    getProgressColorClass(rate) {
      if (rate >= 90) return "bg-emerald";
      if (rate >= 75) return "bg-amber";
      return "bg-rose";
    },

    getStatusBadgeClass(rate) {
      if (rate >= 90) return "status-badge--success";
      if (rate >= 75) return "status-badge--warning";
      return "status-badge--danger";
    },

    getStatusBadgeLabel(rate) {
      if (rate === 100) return "Hoàn thành 100%";
      if (rate >= 90) return "Đạt chỉ tiêu";
      if (rate >= 75) return "Đang thực hiện";
      return "Cần nhắc nhở";
    }
  }
};
</script>

<style scoped>
.evaluation-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

.evaluation-data-state {
  padding: 32px 16px;
  text-align: center;
  color: #64748b;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #dbeafe;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #059669;
  background: #ecfdf5;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #a7f3d0;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

.evaluation-type-switcher {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.evaluation-type-label {
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.evaluation-type-options {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  background: #eef2f8;
  border-radius: 11px;
}

.evaluation-type-button {
  padding: 8px 14px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.evaluation-type-button.active {
  background: #fff;
  color: #2563eb;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
}

/* Bộ lọc theo loại nhận xét và phạm vi thống kê. */
.filter-control-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 16px;
  margin-bottom: 22px;
}

.filter-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.filter-select {
  width: 185px;
}

.filter-date-picker {
  width: 145px;
}

.filter-number-picker {
  width: 120px;
}

.reset-scope-btn {
  font-size: 12px;
  color: #6366f1;
  font-weight: 600;
  padding: 0;
}

.filter-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.period-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.period-button-group {
  display: inline-flex;
  background: #e2e8f0;
  padding: 2px;
  border-radius: 10px;
}

.p-btn {
  border: none;
  background: transparent;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.p-btn.active {
  background: #ffffff;
  color: #2563eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 2. Cụm 4 Chỉ Số KPI */
.eval-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.eval-kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  transition: all 0.2s ease;
}

.eval-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
}

.kpi-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.kpi-details {
  flex: 1;
  min-width: 0;
}

.kpi-name {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-number {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 4px;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

.kpi-hint {
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Card Borders & Colors */
.border-indigo { border-left: 4px solid #4f46e5; }
.border-emerald { border-left: 4px solid #059669; }
.border-rose { border-left: 4px solid #ef4444; }
.border-purple { border-left: 4px solid #8b5cf6; }

.text-indigo { color: #4f46e5; }
.text-emerald { color: #059669; }
.text-rose { color: #ef4444; }
.text-purple { color: #8b5cf6; }

.bg-indigo-soft { background: #e0e7ff; }
.bg-emerald-soft { background: #ecfdf5; }
.bg-rose-soft { background: #ffe4e6; }
.bg-purple-soft { background: #f3e8ff; }

/* 3. Charts */
.charts-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.charts-row::before,
.charts-row::after {
  display: none;
}

.charts-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.chart-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.h-100 {
  height: 100%;
}

.chart-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.chart-title {
  margin: 0 0 3px;
  font-size: 15.5px;
  font-weight: 700;
  color: #1e293b;
}

.chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.tag-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;
  white-space: nowrap;
}

.tag-pill--emerald { background: #dcfce7; color: #15803d; }
.tag-pill--amber { background: #fef3c7; color: #b45309; }

.chart-action-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.scroll-hint-chip {
  font-size: 11.5px;
  font-weight: 600;
  color: #0284c7;
  background: #e0f2fe;
  padding: 4px 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.benchmark-badge {
  font-size: 12px;
  font-weight: 600;
  color: #4338ca;
  background: #e0e7ff;
  padding: 5px 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.chart-container {
  width: 100%;
  height: 250px;
  position: relative;
}

.chart-container-large {
  width: 100%;
  height: 330px;
  position: relative;
}

/* Gauge Footer Breakdown */
.gauge-footer-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.mini-stat-card {
  text-align: center;
  background: #ffffff;
  padding: 8px 4px;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
}

.ms-label {
  display: block;
  font-size: 10.5px;
  color: #64748b;
  margin-bottom: 3px;
}

.ms-val {
  font-size: 13px;
  font-weight: 800;
}

/* Comparison Legend */
.comparison-legend-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 18px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.legend-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.chip-success { background: #10b981; }
.chip-warning { background: #f59e0b; }
.chip-danger { background: #f43f5e; }
.chip-line-benchmark {
  width: 16px;
  height: 2px;
  background: #6366f1;
  border-radius: 999px;
}

/* 4. Table Tiến độ chi tiết */
.class-progress-table-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px 20px;
}

.table-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-card-head.is-expanded {
  margin-bottom: 14px;
}

.table-toggle-btn {
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.table-card-title {
  margin: 0 0 2px;
  font-size: 14.5px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-card-subtitle {
  font-size: 11.5px;
  color: #94a3b8;
}

.table-count-badge {
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  background: #e2e8f0;
  padding: 4px 10px;
  border-radius: 999px;
}

.table-responsive-wrap {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
}

.modern-progress-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  text-align: left;
}

.modern-progress-table thead {
  position: sticky;
  top: 0;
  background: #f1f5f9;
  z-index: 2;
}

.modern-progress-table th {
  padding: 11px 14px;
  color: #475569;
  font-weight: 700;
  font-size: 11.5px;
  border-bottom: 1px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modern-progress-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.modern-progress-table tbody tr:hover {
  background: #f8fafc;
}

.modern-progress-table tr.row-highlight {
  background: #eef2ff !important;
}

.class-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-active-class {
  font-size: 10px;
  font-weight: 700;
  background: #6366f1;
  color: #fff;
  padding: 2px 6px;
  border-radius: 6px;
}

.grade-pill {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 3px 8px;
  border-radius: 6px;
}

.text-secondary {
  color: #64748b;
  font-size: 11.5px;
}

.text-center { text-align: center; }
.font-mono { font-family: monospace; font-weight: 600; }
.font-semibold { font-weight: 600; }

.progress-bar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-track {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.bg-emerald { background: #10b981; }
.bg-amber { background: #f59e0b; }
.bg-rose { background: #ef4444; }

.progress-rate-text {
  width: 42px;
  font-size: 12px;
  text-align: right;
  color: #334155;
}

.status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  display: inline-block;
  white-space: nowrap;
}

.status-badge--success { background: #dcfce7; color: #15803d; }
.status-badge--warning { background: #fef3c7; color: #b45309; }
.status-badge--danger { background: #ffe4e6; color: #b91c1c; }

/* Responsive */
@media (max-width: 1200px) {
  .eval-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .evaluation-type-options {
    width: 100%;
  }
  .evaluation-type-button {
    flex: 1 1 40%;
  }
  .filter-control-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  .eval-kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
